import { useTranslate } from 'ra-core';
import { BulkOperationDialogGetTemplate } from './BulkOperationDialogGetTemplate';
import { useCallback, useEffect, useState } from 'react';
import { BulkOperationDialogUpload } from './BulkOperationDialogUpload';
import { BulkOperationDialogGrid } from './BulkOperationDialogGrid';
import {
  type CsvRecord,
  type GridRowValidator,
  type RowProcessor,
} from './types';
import { BulkOperationDialogProcess } from './BulkOperationDialogProcess';
import {
  BulkOperationDialogStepper,
  type BulkOperationDialogSteps,
} from './BulkOperationDialogStepper';
import { type ZodCsvSchema } from './bulkZodUtils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { cn } from '../../lib/utils';

export interface BulkOperationDialogProps<T extends CsvRecord> {
  label: string;
  resource: string;
  open: boolean;
  onClose: () => void;
  /**
   * Makes sure that the properties in this schema will be included in the exported CSV.
   */
  schema: ZodCsvSchema<T>;
  /**
   * Example values to be included in the exported CSV.
   */
  exampleValues?: Partial<T>[];

  validateRow?: GridRowValidator<T>;
  validationRateLimitDelay: number;

  processRow: RowProcessor<T>;
  processingRateLimitDelay: number;
}

export function BulkOperationDialog<T extends CsvRecord>({
  open,
  onClose,
  label,
  resource,
  validateRow,
  schema,
  exampleValues,
  validationRateLimitDelay,
  processRow,
  processingRateLimitDelay,
}: BulkOperationDialogProps<T>) {
  const translate = useTranslate();
  const [stage, setStage] = useState<BulkOperationDialogSteps>('get-template');
  const [csvData, setCsvData] = useState<T[]>([]);

  const handleClose = useCallback(() => {
    if (
      stage === 'grid' &&
      !confirm(
        translate('moonstar.bulk_operation.confirmDiscard', {
          _: 'Discard changes?',
        })
      )
    ) {
      return;
    } else if (
      stage === 'process' &&
      !confirm(
        translate('moonstar.bulk_operation.confirmStop', {
          _: 'Stop processing?',
        })
      )
    ) {
      return;
    }

    onClose();
  }, [onClose, stage, translate]);

  useEffect(() => {
    function handler(event: BeforeUnloadEvent) {
      if (stage === 'grid' || stage === 'process') {
        event.preventDefault();
      }
    }

    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [stage]);

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          handleClose();
        }
      }}
    >
      <DialogContent
        className={cn(
          'min-h-0 overflow-hidden p-0',
          stage === 'grid'
            ? 'h-[50vh] max-h-[calc(100vh-2rem)] grid-rows-[minmax(0,1fr)] sm:max-w-[min(66.5vw,84rem)]'
            : 'sm:max-w-[min(95vw,48rem)]'
        )}
      >
        <div className="flex min-h-0 min-w-0">
          <BulkOperationDialogStepper step={stage} />
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            <DialogHeader className="shrink-0 border-b px-6 py-4">
              <DialogTitle>{translate(label, { _: label })}</DialogTitle>
            </DialogHeader>

            {stage === 'get-template' && (
              <BulkOperationDialogGetTemplate
                label={label}
                resource={resource}
                onBack={onClose}
                onNext={() => setStage('upload')}
                schema={schema}
                exampleValues={exampleValues}
              />
            )}
            {stage === 'upload' && (
              <BulkOperationDialogUpload
                onBack={() => setStage('get-template')}
                onNext={(data) => {
                  setCsvData(data as T[]);
                  setStage('grid');
                }}
              />
            )}
            {stage === 'grid' && (
              <BulkOperationDialogGrid
                onBack={() => {
                  if (
                    confirm(
                      translate('moonstar.bulk_operation.confirmDiscard', {
                        _: 'Discard changes?',
                      })
                    )
                  ) {
                    setStage('upload');
                  }
                }}
                onNext={(data) => {
                  setCsvData(data);
                  setStage('process');
                }}
                label={label}
                data={csvData}
                schema={schema}
                validationRateLimitDelay={validationRateLimitDelay}
                validateRow={validateRow}
              />
            )}
            {stage === 'process' && (
              <BulkOperationDialogProcess
                label={label}
                data={csvData}
                processRow={processRow}
                processingRateLimitDelay={processingRateLimitDelay}
                onNext={onClose}
              />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
